import React, { useState, useEffect, forwardRef, useImperativeHandle } from 'react';
import ReactDOM from 'react-dom';
import axios from 'axios';
import GetCookie from '../../utils/GetCookie';
import { StatusBadgeXSm } from '../../utils/StatusBadge';
import PaginationControls from '../../utils/PaginationControls';
import TrimContent from '../../utils/TrimContent';
import authNavigate from '../../globalComponents/AuthNav';
import { useNavigate } from 'react-router-dom';

const GenbiTemplateList = forwardRef(({ 
  onEditTemplate, 
  onOpenTemplate, 
  onRefresh, 
  onDeleteTemplate, 
  onDeleteRequest,
  openTemplateIds, 
  onError,
  isFormVisible, 
  editingTemplateId,
  editingTemplateName,
  isFormDirty,
  onSaveTemplate,
  slrFilterMode = false,        
  genbiFabricId = null,           
  slrTemplateName = null,        
  onClearFilter = null,          
  onProcessingStart,
  onProcessingEnd
}, ref) => {
  const [templates, setTemplates] = useState([]);
  const [expandedFabrics, setExpandedFabrics] = useState({});
  const [page, setPage] = useState(1);
  const [size, setSize] = useState(25);
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [total, setTotal] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [showDirtyModal, setShowDirtyModal] = useState(false);
  const [pendingEdit, setPendingEdit] = useState(null);
  useEffect(() => {
  if (slrFilterMode && genbiFabricId) {
    setExpandedFabrics({ [genbiFabricId]: true });
  }
}, [slrFilterMode, genbiFabricId]);

  const navigate = useNavigate();

  const API_BASE_URL = '/pceapi/genbi';
  const headers = {
    Authorization: GetCookie('access_token')
  };

  const fetchTemplates = async () => {
    setIsLoading(true);
  
    try {
      console.log("🔍 GenbiTemplateList fetchTemplates called with:");
      console.log("   genbiFabricId:", genbiFabricId);
      console.log("   slrFilterMode:", slrFilterMode);
      console.log("   slrTemplateName:", slrTemplateName);
      // Report templates only (Canvas is not used) — the backend lists reports only.
      const requestBody = {
        page,
        size,
        search: search || undefined
      };
      
      if (genbiFabricId !== null) {
        requestBody.filter_fabric_id = genbiFabricId;
        console.log("🔍 GenbiTemplateList requestBody:", requestBody);
      }

      if (slrTemplateName) {
        requestBody.filter_template_name = slrTemplateName;
      }

      
      const response = await axios.post(
        `${API_BASE_URL}/list-templates`,
        requestBody,
        { headers }
      );

      const data = response.data;

      if (data.status === 'NotAuthorized') {
        authNavigate('/login', 'iam', navigate);
        return;
      }

      if (data.status === 'failed') {
        onError(data.message || 'Failed to load templates');
        return;
      }

      setTemplates(data.data || []);
      setTotal(data.total || 0);
    } catch (error) {
      console.error('Error fetching templates:', error);
      onError(error.message || 'Failed to load templates');
    } finally {
      setIsLoading(false);
    }
  };

  useImperativeHandle(ref, () => ({
    fetchTemplates
  }));

  // Expose fetchTemplates to parent component via ref
  useEffect(() => {
  console.log("🔍 GenbiTemplateList useEffect triggered", {
    slrFilterMode,
    genbiFabricId,
    slrTemplateName
  });
  
  // ✅ Wait for fabric ID if in SLR mode
  if (slrFilterMode) {
    if (!genbiFabricId) {
      console.log("⏳ SLR mode active but no fabricId yet - skipping fetch");
      return;
    }
  }
  
  console.log("🚀 Fetching templates now");
  fetchTemplates();
}, [page, size, search, genbiFabricId, slrFilterMode]);

  // Toggle fabric expansion
  const toggleFabricExpansion = (fabricId) => {
    setExpandedFabrics(prev => ({
      ...prev,
      [fabricId]: !prev[fabricId]
    }));
  };

  // Handle search input change
  const handleSearchInputChange = (e) => {
    setSearchInput(e.target.value);
  };

  // Handle search button click
  const handleSearch = () => {
    setSearch(searchInput);
    setPage(1);
  };

  // Handle Enter key press in search input
  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  // Handle edit button click
  const handleEditClick = async (template, fabricId, fabricName) => {
    if (isFormVisible && isFormDirty) {
      setPendingEdit({ template, fabricId, fabricName });
      setShowDirtyModal(true);
        return;
    }

    try {
      await onEditTemplate(template, fabricId, fabricName);
    } catch (error) {
      onError('Failed to load template details');
    }
  };
  const handleConfirmDiscard = async () => {
    setShowDirtyModal(false);
    if (pendingEdit) {
      try {
        await onEditTemplate(pendingEdit.template, pendingEdit.fabricId, pendingEdit.fabricName);
      } catch (error) {
        onError('Failed to load template details');
      }
      setPendingEdit(null);
    }
  };

  const handleConfirmSave = () => {
    setShowDirtyModal(false);
    if (onSaveTemplate) onSaveTemplate(pendingEdit);
    setPendingEdit(null);
  };
  // Handle delete click
  const handleDeleteClick = (template, fabricId, fabricName) => {
    if (onDeleteRequest) {
      onDeleteRequest(template, fabricId, fabricName);
    }
  };

  return (
    <div className="sidebar-inner ev-card-2">
      {/* THIS FILTER INDICATOR */}
    
      <div className="d-flex justify-content-between align-items-center mb-2">
        <p className="ev-reg-p-dark ev-bold-reg mb-0">
          Templates{!slrFilterMode && <span> ({total} Projects)</span>}
        </p>
      </div>
      
      <div className="d-flex justify-content-between">
        <div className="mb-2 col-8 position-relative">
          <input
            type="text"
            className="form-control search-input ms-0"
            placeholder="Search Templates"
            value={searchInput}
            onChange={handleSearchInputChange}
            onKeyPress={handleKeyPress}
            disabled={slrFilterMode}
          />
          <button 
            className="ev-btn-lightblue-fill-round search-bar-button" 
            onClick={handleSearch}
            disabled={slrFilterMode}
          >
            <i className="bi bi-search"></i>
          </button>
        </div>
        <div className="gap2 mt-1"> 
          <button 
              className="ev-btn-lightblue-fill-round ev-bold-reg ms-2" 
              onClick={fetchTemplates}
              disabled={isLoading}
            >
             <i className="bi bi-arrow-repeat"></i>  Refresh 
          </button>
        </div>
      </div>

      <div className="tree-container mt-2 scrollable-element">
        
        {slrFilterMode && genbiFabricId === null && (
          <div className="text-center p-3 mt-5">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            <p className="ev-small-p-blue mt-2">Loading SLR templates...</p>
          </div>
        )}
        
        {isLoading && !(slrFilterMode && genbiFabricId === null) && (
          <div className="text-center p-3 mt-5">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        )}
        
        {!isLoading && templates.length === 0 && (
          <div className="text-center p-3">
            <p className="ev-small-p-blue">No templates found</p>
          </div>
        )}
        
        {!isLoading && templates.length > 0 && (
          <div className="tree-container-inner">
            {templates.filter(fabric => fabric.templates.length > 0).map(fabric => (
              <div key={fabric.id} className="ev-card-1 ev-padding-small mb-2 ev-radius-small">
                <div className="d-flex align-items-center justify-content-between">
                  <div className="d-flex align-items-center">
                    <button 
                      className="btn btn-sm p-0 me-2 ev-fs-20" 
                      onClick={() => toggleFabricExpansion(fabric.id)}
                    >
                      <i className={`ev-reg-p-dark bi ${expandedFabrics[fabric.id] ? 'bi-chevron-down' : 'bi-chevron-right'}`}></i>
                    </button>                        
                    <span className="ev-small-p-blue avatar-tooltip ev-bold-med" data-tooltip={fabric.name}>
                      <i className="bi bi-boxes"></i> &nbsp;
                      <TrimContent text={fabric.name} maxLength={20} />
                    </span>
                  </div>
                  <span className="ev-message-card2 ev-radius-med ev-fs-11 ev-bold-med">
                    {fabric.template_count} Templates
                  </span>
                </div>
                
                {expandedFabrics[fabric.id] && (() => {
                  // Renders a single template card
                  const renderTemplateCard = (template) => (
                        <div key={template.id} className={`${editingTemplateId === template.id ? 'ev-card-5' : 'ev-card-9'} mt-2 ev-radius-small`}>
                              <div className="mb-2 text-end">
                                <span className="ev-message-card3 ev-btn-small ev-xsmall-p-dark mb-1 me-2">
                                    <i className="bi bi-file-earmark-text"></i> Report
                                </span>
                                
                                {/* Open button functionality */}
                                <button 
                                  className={`ev-btn-blue-fill-round ev-btn-small ev-bold-reg ${openTemplateIds?.includes(template.id) ? 'disabled' : ''}`}
                                  onClick={() => onOpenTemplate(template, fabric.id, fabric.name)}
                                  disabled={openTemplateIds?.includes(template.id)}
                                >
                                  {openTemplateIds?.includes(template.id) ? <><i className="bi bi-dash-circle" />{' '}Opened</> : <><i className="bi bi-plus-circle" />{' '}Open</>}
                                </button>
                              
                              <button 
                                className="ev-btn-skyblue-fill-round ev-radius-xsmall pe-2 ps-2 ms-2 avatar-tooltip"
                                onClick={() => handleEditClick(template, fabric.id, fabric.name)}
                                data-tooltip={template.status === 'RUNNING' ? 'Template/Report Running' : 'Edit Template'}
                                disabled={template.status === 'RUNNING'|| template.report_gen_status==='RUNNING'}
                              >
                                <i className="bi bi-pencil"></i>
                              </button>
                              
                              <button 
                                className="ev-btn-red-fill-round ev-radius-xsmall pe-2 ps-2 ms-2 avatar-tooltip"
                                onClick={() => handleDeleteClick(template, fabric.id, fabric.name)}
                                data-tooltip={template.status === 'RUNNING' ? 'Template Running' : 'Delete Template'}
                                disabled={template.status === 'RUNNING'} 
                              >
                                <i className="bi bi-trash"></i>
                              </button>
                            </div>
                            
                            <div className="d-flex align-items-center justify-content-between mb-0">
                              <span className="ev-small-p-dark flex-grow-1 me-2 avatar-tooltip" data-tooltip={template.template_name}> 
                                <i className="bi bi-kanban"></i>
                                &nbsp;
                                  <TrimContent text={template.template_name} maxLength={20} />
                                  &nbsp;
                              </span>       
                              <span className="flex-shrink-0"> 
                                <StatusBadgeXSm status={template.status} />
                              </span>
                            </div>
                        </div>
                  );

                  return (
                    <div>
                      <p className="ev-small-p-blue mb-1 mt-2 ev-bold-med">Templates</p>
                      {fabric.templates.length === 0 ? (
                        <div className="ev-card-2 mt-2">
                          <p className="ev-small-p-blue text-center mb-0">No templates in this project</p>
                        </div>
                      ) : (
                        fabric.templates.map(renderTemplateCard)
                      )}
                    </div>
                  );
                })()}
              </div>
            ))}
          </div>
        )}
      </div>
      
      {/* Pagination for sidebar */}
      <div className="d-flex justify-content-between align-items-center mt-3 sidebar-inner-bottom-fix-ele ev-fs-12">
        <div>
          <button 
            className="ev-btn-light-fill ev-btn-xsmall" 
            disabled={page === 1 || isLoading}
            onClick={() => setPage(page - 1)}
          >
            <i className="bi bi-chevron-left"></i>
          </button>
          
          <span className="ev-xsmall-p-dark ms-2">
            Page {page} of {Math.ceil(total / size) || 1}
          </span>
          
          <button 
            className="ev-btn-light-fill ev-btn-xsmall ms-2" 
            disabled={page >= Math.ceil(total / size) || isLoading}
            onClick={() => setPage(page + 1)}
          >
            <i className="bi bi-chevron-right"></i>
          </button>
        </div>
        <div>
         <PaginationControls 
              initialSize={size}
              onSizeChange={(newSize) => {
                setSize(newSize);
                setPage(1);
              }}
            />
        </div>
      </div>
            {/* Unsaved Changes Modal */}
      {showDirtyModal && ReactDOM.createPortal(
        <div className="popup" onClick={() => setShowDirtyModal(false)}>
          <div className="ev-card-4 ev-dirty-modal p-0" onClick={(e) => e.stopPropagation()}>
            <div className="comment-success-modal-header">
              <p className="mb-0 ev-fs-15 ev-bold-med ev-reg-p-dark">
                Unsaved Changes
              </p>
              <button
                type="button"
                className="ev-btn-text-only-fill ev-fs-20"
                onClick={() => setShowDirtyModal(false)}
              >
                <i className="bi bi-x"></i>
              </button>
            </div>
            <div className="comment-success-modal-body ev-fs-12">
              <p className="ev-reg-p-dark mb-2">
                You have unsaved changes in the current template: <strong>{editingTemplateName}</strong>.
              </p>
              <p className="ev-reg-p-dark mb-1">
                Would you like to save them before switching to another template?
              </p>
              <p className="text-danger mb-0">
                Discarding will permanently lose all unsaved changes.
              </p>
            </div>
            <div className="comment-success-modal-footer justify-content-between">
              <button
                type="button"
                className="ev-btn-red-fill-round ev-bold-reg px-3"
                onClick={handleConfirmDiscard}
              >
                <i className="bi bi-x me-1"></i>
                Discard
              </button>
              <button
                type="button"
                className="ev-btn-blue-fill-round ev-bold-reg px-3"
                onClick={handleConfirmSave}
              >
                <i className="bi bi-floppy me-1"></i>
                Save
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
});

GenbiTemplateList.displayName = 'GenbiTemplateList';

export default GenbiTemplateList;